// The card under the board after an attempt (spec §8.5): calm when failed,
// a small celebration when solved.
import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import type { AttemptResult } from "../api/client";
import { useT } from "../i18n";
import { seconds, signed } from "../lib/format";
import { RollingNumber } from "./RollingNumber";

type Props = {
  result: AttemptResult;
  isFavorite: boolean;
  canFavorite: boolean;
  canRetry: boolean;
  onFavorite: () => void;
  onReplay: () => void;
  onNext: () => void;
  onRetry: () => void;
  onReport?: () => void;
  showingSolution: boolean;
  /** Counting down to the next exercise: a thin bar runs out over this time. */
  autoNextMs?: number | null;
  /** The user did something with the result: stop the countdown. */
  onStay?: () => void;
};

function Burst() {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return null;
  return (
    <span aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: 12 }}>
      {Array.from({ length: 14 }, (_, i) => {
        const angle = (i / 14) * Math.PI * 2;
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x: Math.cos(angle) * 90, y: Math.sin(angle) * 50, opacity: 0, scale: 0.4 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            style={{
              position: "absolute",
              left: "50%",
              top: 34,
              width: 7,
              height: 7,
              borderRadius: 2,
              background: i % 2 ? "var(--accent)" : "var(--success)",
            }}
          />
        );
      })}
    </span>
  );
}

export function ResultCard(p: Props) {
  const t = useT();
  const r = p.result;
  const reveal = r.placement?.done ? r.placement : null;
  const rating = r.rating;
  const nextRef = useRef<HTMLButtonElement>(null);
  useEffect(() => nextRef.current?.focus({ preventScroll: true }), []);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  return (
    <motion.div
      className="result"
      style={{ position: "relative" }}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduced ? { duration: 0.08 } : { type: "spring", stiffness: 420, damping: 32, mass: 0.8 }}
      role="status"
      aria-live="polite"
      onPointerDownCapture={(e) => {
        if (!(e.target as HTMLElement).closest(".next")) p.onStay?.();
      }}
    >
      {p.autoNextMs ? (
        <span className="autonext" aria-hidden style={{ animationDuration: `${p.autoNextMs}ms` }} />
      ) : null}
      {r.milestones.length > 0 && <Burst />}
      {reveal ? (
        <div className="reveal">
          <div className="muted">{t("result.placement_done")}</div>
          <div className="display">
            <RollingNumber from={1000} to={reveal.rating ?? 1500} ms={1400} />
          </div>
          <div>{t(`level.${reveal.description}`)}</div>
        </div>
      ) : (
        <>
          <div className="head">
            <span className="mark" style={{ color: r.solved ? "var(--success)" : "var(--muted)" }}>{r.solved ? "✓" : "○"}</span>
            <span className="what">
              {r.solved ? t("result.solved_in", { t: seconds(r.think_ms) }) : t("result.not_solved")}
            </span>
            {rating && (
              <span className="delta display" style={{ color: rating.delta >= 0 ? "var(--success)" : "var(--muted)" }}>
                {signed(rating.delta)}
              </span>
            )}
          </div>
          <div className="sub">
            <span>{r.faster_than != null && r.solved ? t("result.faster", { p: r.faster_than }) : !r.rated ? t("result.unrated") : r.placement ? t("train.placement", { i: r.placement.index, n: r.placement.total }) : ""}</span>
            {rating && (
              <span className="num">
                {rating.before} → <RollingNumber from={rating.before} to={rating.after} />
              </span>
            )}
          </div>
        </>
      )}
      <div className="feedback">
        {r.feedback.map((f, i) => (
          <div key={i}>{t(`fb.${f.key}`, f.params as Record<string, string>)}</div>
        ))}
        {r.milestones.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {r.milestones.map((m, i) => (
              <motion.span
                key={i}
                className="badge"
                initial={reduced ? { opacity: 0 } : { rotateX: 90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                transition={{ delay: 0.25 + i * 0.12, duration: reduced ? 0.08 : 0.45 }}
              >
                ✦ {t(`ms.${m.key}`, m.params as Record<string, string>)}
              </motion.span>
            ))}
          </div>
        )}
      </div>
      <div className="actions">
        {p.canFavorite && (
          <button className="ghost" onClick={p.onFavorite} aria-pressed={p.isFavorite}>
            {p.isFavorite ? "★" : "☆"} {t("train.favorite")}
          </button>
        )}
        {r.solved ? (
          <button className="ghost" onClick={p.onReplay} disabled={p.showingSolution}>↻ {t("result.replay")}</button>
        ) : (
          <>
            <button className="ghost" onClick={p.onReplay} disabled={p.showingSolution}>{t("result.show")}</button>
            {p.canRetry && <button className="ghost" onClick={p.onRetry}>{t("result.retry")} <kbd>R</kbd></button>}
          </>
        )}
        {p.onReport && <button className="ghost small muted" onClick={p.onReport}>{t("train.report")}</button>}
        <button className="primary next" onClick={p.onNext} ref={nextRef}>
          ▶ {t("result.next")} <kbd>N</kbd>
        </button>
      </div>
    </motion.div>
  );
}
